import type { DeploymentListResponse, DeviceResponseV5 } from '@emma/api_client/types';
import { Accordion, Box, Flex, Grid, Link, Tag, Text, Tooltip } from '@lego/klik-ui';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useDeployments } from '@/hooks/deployment';
import { useFleet } from '@/hooks/fleet';
import { dateTimeFormat } from '@/utility/date';
import { paths } from '@/utility/global_paths';
import { Loader, TextHeader } from '../common';
import { DeviceApplicationStatus } from './DeviceApplicationStatus';
import { DeviceDeploymentIssues } from './DeviceDeploymentIssues';
import { StatusIcon } from './icons/StatusIcon';

type Props = {
  device: DeviceResponseV5;
};

export const DeviceDeployment = ({ device }: Props) => {
  if (!device.fleets || device.fleets.length === 0) {
    return (
      <Box background="white" boxShadow="md" padding={6} rounded="md" width="full">
        <TextHeader mb={1} tagLevel="h3" title="Device Deployments" />
        <Text color="gray.600" fontSize="sm" mb={2}>
          Shows the deployment state for each fleet this device is assigned to, including the
          desired and reported deployment status
        </Text>
        <Text variant="p">No fleets assigned to this device</Text>
      </Box>
    );
  }

  return (
    <Box background="white" boxShadow="md" padding={6} rounded="md" width="full">
      <TextHeader mb={1} tagLevel="h3" title="Device Deployments" />
      <Text color="gray.600" fontSize="sm" mb={4}>
        Shows the deployment state for each fleet this device is assigned to, including the desired
        and reported deployment status
      </Text>
      <FleetsList device={device} fleets={device.fleets} workspaceId={device.workspaceId} />
    </Box>
  );
};

type Fleet = {
  id: number;
  workspaceId: number;
};

type FleetsListProps = {
  workspaceId: number;
  fleets: Fleet[];
  device: DeviceResponseV5;
};

const FleetsList = ({ workspaceId, fleets, device }: FleetsListProps) => {
  const ownedFleets = fleets.filter((fleet) => fleet.workspaceId === workspaceId);
  const invitedFleets = fleets.filter((fleet) => fleet.workspaceId !== workspaceId);

  return (
    <Box>
      {ownedFleets.length > 0 && (
        <Box mb={6}>
          <Accordion allowMultiple>
            {ownedFleets.map((fleet) => (
              <OwnedFleetCard
                device={device}
                fleet={fleet}
                key={fleet.id}
                workspaceId={workspaceId}
              />
            ))}
          </Accordion>
        </Box>
      )}

      {invitedFleets.length > 0 && (
        <Box>
          <TextHeader mb={3} tagLevel="h4" title="Cohosted Fleets" />
          <Accordion allowMultiple>
            {invitedFleets.map((fleet) => (
              <InvitedFleetCard
                device={device}
                fleet={fleet}
                key={fleet.id}
                workspaceId={workspaceId}
              />
            ))}
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

type OwnedFleetCardProps = {
  workspaceId: number;
  fleet: Fleet;
  device: DeviceResponseV5;
};

const OwnedFleetCard = ({ workspaceId, fleet, device }: OwnedFleetCardProps) => {
  const {
    data: deploymentsData,
    error: deploymentsError,
    isLoading: isDeploymentsLoading,
  } = useDeployments(fleet.workspaceId, fleet.id, {}, { showForbiddenErrorToast: false });
  const {
    data: fleetData,
    error,
    isLoading,
  } = useFleet(workspaceId, fleet.id, {
    showForbiddenErrorToast: false,
  });

  if (isLoading || isDeploymentsLoading) {
    return (
      <Box background="gray.50" borderRadius="md" padding={4}>
        <Loader />
      </Box>
    );
  }

  if (error || deploymentsError || !fleetData || !deploymentsData) {
    return (
      <Box background="red.50" borderRadius="md" padding={4}>
        <Text color="red.700" fontWeight="medium">
          Fleet {fleet.id} - Failed to load
        </Text>
        <Text color="red.600" fontSize="sm" mt={1}>
          Failed to load fleet information
        </Text>
      </Box>
    );
  }

  const deviceDeployment = device.deployments?.find(
    (deployment) => deployment.fleetId === fleet.id && deployment.isActive
  );

  const reportedStatus = deviceDeployment?.latestState ?? null;
  const reportedTimestamp = deviceDeployment?.deploymentStatusUpdatedAt ?? null;
  const failureReason = deviceDeployment?.failureReason ?? null;
  const isHealthy = reportedStatus === 'SUCCEEDED';
  const activeDeploymentId = fleetData.activeDeploymentId;
  const isOutdated =
    isHealthy &&
    activeDeploymentId != null &&
    deviceDeployment?.deploymentId !== activeDeploymentId;

  return (
    <Accordion.Item value={fleet.id}>
      <Accordion.Button>
        <Flex align="center" justify="space-between" width="full">
          <Box>
            <Text fontWeight="medium">{fleetData.name}</Text>

            <Flex alignItems="baseline" direction="row" gap="4px">
              <Text color="gray" fontSize="sm">
                Fleet ID: {fleet.id} • Workspace {fleet.workspaceId} • Status{' '}
              </Text>
              <StatusIcon
                isOutdated={isOutdated}
                isSuccess={isHealthy}
                reason={
                  isOutdated
                    ? `The deployment is running but not up to date. A newer deployment (${activeDeploymentId}) is available`
                    : !isHealthy && failureReason
                      ? `The deployment failed with reason: ${failureReason}`
                      : isHealthy
                        ? 'The deployment was successfully deployed'
                        : 'Deployment status unknown'
                }
                timestamp={reportedTimestamp ?? undefined}
              />
            </Flex>
          </Box>
          <Accordion.Icon />
        </Flex>
      </Accordion.Button>
      <Accordion.Panel>
        <FleetNavigationLinks {...fleet} />
        <DeploymentSummary
          deploymentsData={deploymentsData.data}
          device={device}
          fleetId={fleet.id}
          workspaceId={fleet.workspaceId}
        />
        <Box mt={6}>
          <DeviceApplicationStatus device={device} fleetId={fleet.id} />
        </Box>
        <Box mt={6}>
          <DeviceDeploymentIssues device={device} fleetId={fleet.id} />
        </Box>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

type InvitedFleetCardProps = {
  workspaceId: number;
  fleet: Fleet;
  device: DeviceResponseV5;
};

const InvitedFleetCard = ({ fleet, device }: InvitedFleetCardProps) => {
  const {
    data: deploymentsData,
    error: deploymentsError,
    isLoading: isDeploymentsLoading,
  } = useDeployments(fleet.workspaceId, fleet.id, {}, { showForbiddenErrorToast: false });
  const {
    data: fleetData,
    error,
    isLoading,
  } = useFleet(fleet.workspaceId, fleet.id, {
    showForbiddenErrorToast: false,
  });
  const itemId = `fleet-${fleet.id}`;

  if (isLoading || isDeploymentsLoading) {
    return (
      <Accordion.Item value={itemId}>
        <Accordion.Button>
          <Flex align="center" justify="space-between" width="full">
            <Text fontWeight="medium">Fleet {fleet.id}</Text>
            <Accordion.Icon />
          </Flex>
        </Accordion.Button>
        <Accordion.Panel>
          <FleetNavigationLinks {...fleet} />
          <Loader />
        </Accordion.Panel>
      </Accordion.Item>
    );
  }

  if (error || deploymentsError || !fleetData || !deploymentsData) {
    return (
      <Accordion.Item value={itemId}>
        <Accordion.Button>
          <Flex align="center" justify="space-between" width="full">
            <Text color="red" fontWeight="medium">
              Fleet {fleet.id} - Failed to load
            </Text>
            <Accordion.Icon />
          </Flex>
        </Accordion.Button>
        <Accordion.Panel>
          <FleetNavigationLinks {...fleet} />
          <Text color="red">Failed to load fleet information</Text>
        </Accordion.Panel>
      </Accordion.Item>
    );
  }

  const deviceDeployment = device.deployments?.find(
    (deployment) => deployment.fleetId === fleet.id && deployment.isActive
  );

  const reportedStatus = deviceDeployment?.latestState ?? null;
  const reportedTimestamp = deviceDeployment?.deploymentStatusUpdatedAt ?? null;
  const failureReason = deviceDeployment?.failureReason ?? null;
  const isHealthy = reportedStatus === 'SUCCEEDED';
  const activeDeploymentId = fleetData.activeDeploymentId;
  const isOutdated =
    isHealthy &&
    activeDeploymentId != null &&
    deviceDeployment?.deploymentId !== activeDeploymentId;

  return (
    <Accordion.Item value={itemId}>
      <Accordion.Button>
        <Flex align="center" justify="space-between" width="full">
          <Box>
            <Text fontWeight="medium">{fleetData.name}</Text>

            <Flex alignItems="baseline" direction="row" gap="4px">
              <Text color="gray" fontSize="sm">
                Fleet ID: {fleet.id} • Workspace {fleet.workspaceId} • Status{' '}
              </Text>
              <StatusIcon
                isOutdated={isOutdated}
                isSuccess={isHealthy}
                reason={
                  isOutdated
                    ? `The deployment is running but not up to date. A newer deployment (${activeDeploymentId}) is available`
                    : !isHealthy && failureReason
                      ? `The deployment failed with reason: ${failureReason}`
                      : isHealthy
                        ? 'The deployment was successfully deployed'
                        : 'Deployment status unknown'
                }
                timestamp={reportedTimestamp ?? undefined}
              />
            </Flex>
          </Box>
          <Accordion.Icon />
        </Flex>
      </Accordion.Button>
      <Accordion.Panel>
        <FleetNavigationLinks {...fleet} />
        <DeploymentSummary
          deploymentsData={deploymentsData.data}
          device={device}
          fleetId={fleet.id}
          workspaceId={fleet.workspaceId}
        />
        <Box mt={6}>
          <DeviceApplicationStatus device={device} fleetId={fleet.id} />
        </Box>
        <Box mt={6}>
          <DeviceDeploymentIssues device={device} fleetId={fleet.id} />
        </Box>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

const FleetNavigationLinks = ({ id: fleetId, workspaceId }: Fleet) => (
  <Flex gap={4} mb={4}>
    <Link as={ReactRouterLink} p={0} to={paths.fleetById(workspaceId, fleetId)}>
      Fleet #{fleetId}
    </Link>
    <Link as={ReactRouterLink} p={0} to={paths.workspaceById(workspaceId)}>
      Workspace #{workspaceId}
    </Link>
  </Flex>
);

type DeploymentSummaryProps = {
  workspaceId: number;
  fleetId: number;
  device: DeviceResponseV5;
  deploymentsData: DeploymentListResponse;
};

const DeploymentSummary = ({
  workspaceId,
  fleetId,
  device,
  deploymentsData,
}: DeploymentSummaryProps) => {
  const {
    data: fleetData,
    error: fleetError,
    isLoading: isFleetLoading,
  } = useFleet(workspaceId, fleetId, { showForbiddenErrorToast: false });

  if (isFleetLoading) {
    return (
      <Box background="white" padding={4}>
        <Loader />
      </Box>
    );
  }

  if (fleetError || !fleetData || !deploymentsData) {
    return (
      <Box background="white" padding={4}>
        <Text color="red.600">Failed to load deployment information</Text>
      </Box>
    );
  }

  const activeDeploymentId = fleetData.activeDeploymentId;

  if (!activeDeploymentId) {
    return (
      <Box background="white" padding={4}>
        <Text color="gray.600">No active deployment for this fleet</Text>
      </Box>
    );
  }

  const activeDeployment = deploymentsData.deployments.find((d) => d.id === activeDeploymentId);

  const deviceDeployment = device.deployments?.find(
    (deployment) => deployment.fleetId === fleetId && deployment.isActive
  );

  return (
    <Flex direction={{ base: 'column', md: 'row' }} gap={4} padding={4}>
      <DesiredDeploymentSection
        activeDeployment={activeDeployment}
        activeDeploymentId={activeDeploymentId}
        deviceDeployment={deviceDeployment}
        fleetId={fleetId}
        workspaceId={workspaceId}
      />
      <ReportedDeploymentSection
        deviceDeployment={deviceDeployment}
        fleetId={fleetId}
        workspaceId={workspaceId}
      />
    </Flex>
  );
};

type DesiredDeploymentSectionProps = {
  workspaceId: number;
  activeDeploymentId: number;
  fleetId: number;
  activeDeployment?: {
    applicationVersionId: number | null;
    applicationId: number | null;
    createdAt: string;
  };
  deviceDeployment?: { deploymentId: number };
};

const DesiredDeploymentSection = ({
  workspaceId,
  activeDeploymentId,
  fleetId,
  activeDeployment,
  deviceDeployment,
}: DesiredDeploymentSectionProps) => {
  const inSync = activeDeploymentId === deviceDeployment?.deploymentId;

  return (
    <Box border="1px solid" borderColor="slate.200" borderRadius="md" flex={1} p={4}>
      <Flex align="center" gap={2} mb={1}>
        <TextHeader tagLevel="h4" title="Desired by Fleet" />
        {inSync ? (
          <Tooltip label="Device is running the latest active deployment">
            <Tag colorScheme="success" size="sm">
              In sync
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip label="Device is behind on deployment. Verify that device is online, and check device logs for errors.">
            <Tag colorScheme="error" size="sm">
              Out of sync
            </Tag>
          </Tooltip>
        )}
      </Flex>
      <Text color="gray.600" fontSize="sm" mb={3}>
        The deployment that the fleet expects this device to be running
      </Text>
      <Grid gap={3} templateColumns="auto 1fr">
        <Text fontWeight="medium">Deployment ID</Text>
        <Link
          as={ReactRouterLink}
          p={0}
          to={paths.deploymentById(workspaceId, fleetId, activeDeploymentId)}
        >
          {activeDeploymentId}
        </Link>
        <Text fontWeight="medium">App Version ID</Text>
        <Box>
          {activeDeployment?.applicationVersionId ? (
            <Link
              as={ReactRouterLink}
              p={0}
              to={paths.applicationVersionById(
                workspaceId,
                activeDeployment.applicationId,
                activeDeployment.applicationVersionId
              )}
            >
              {activeDeployment.applicationVersionId}
            </Link>
          ) : (
            <Text color="gray.400">—</Text>
          )}
        </Box>
        <Text fontWeight="medium">Application ID</Text>
        <Box>
          {activeDeployment?.applicationId ? (
            <Link
              as={ReactRouterLink}
              p={0}
              to={paths.applicationById(workspaceId, activeDeployment.applicationId)}
            >
              {activeDeployment.applicationId}
            </Link>
          ) : (
            <Text color="gray.400">—</Text>
          )}
        </Box>
        <Text fontWeight="medium">Created at</Text>
        <Text fontFamily="mono" fontSize="sm">
          {activeDeployment?.createdAt
            ? new Date(activeDeployment.createdAt).toLocaleString(dateTimeFormat.local)
            : '—'}
        </Text>
      </Grid>
    </Box>
  );
};

type ReportedDeploymentSectionProps = {
  workspaceId: number;
  fleetId: number;
  deviceDeployment?: {
    deploymentId: number;
    latestState?: string | null;
    deploymentStatusUpdatedAt?: string | null;
    failureReason?: string | null;
  };
};

const ReportedDeploymentSection = ({
  workspaceId,
  fleetId,
  deviceDeployment,
}: ReportedDeploymentSectionProps) => {
  const reportedDeploymentId = deviceDeployment?.deploymentId ?? null;
  const reportedStatus = deviceDeployment?.latestState ?? null;
  const reportedTimestamp = deviceDeployment?.deploymentStatusUpdatedAt ?? null;
  const failureReason = deviceDeployment?.failureReason ?? null;
  const isHealthy = reportedStatus === 'SUCCEEDED';

  return (
    <Box border="1px solid" borderColor="slate.200" borderRadius="md" flex={1} p={4}>
      <Flex align="center" gap={2} mb={1}>
        <TextHeader tagLevel="h4" title="Reported by Device" />
        {reportedStatus && reportedTimestamp && (
          <StatusIcon
            isSuccess={isHealthy}
            reason={
              !isHealthy && failureReason
                ? `The deployment failed with reason: ${failureReason}`
                : isHealthy
                  ? 'The deployment was successfully deployed'
                  : 'Deployment status unknown'
            }
            timestamp={reportedTimestamp}
          />
        )}
      </Flex>
      <Text color="gray.600" fontSize="sm" mb={3}>
        The deployment currently reported as running on the device
      </Text>
      <Grid alignItems="center" gap={3} templateColumns="auto 1fr">
        <Text fontWeight="medium">Deployment ID</Text>
        <Box>
          {reportedDeploymentId ? (
            <Link
              as={ReactRouterLink}
              p={0}
              to={paths.deploymentById(workspaceId, fleetId, reportedDeploymentId)}
            >
              {reportedDeploymentId}
            </Link>
          ) : (
            <Text color="gray.400">Not reported</Text>
          )}
        </Box>
        <Text fontWeight="medium">Status</Text>
        <Box>
          {reportedStatus ? (
            <Tag colorScheme={reportedStatus === 'SUCCEEDED' ? 'success' : 'error'} size="sm">
              {reportedStatus}
            </Tag>
          ) : (
            <Text color="gray.400">Not reported</Text>
          )}
        </Box>
        {reportedTimestamp && (
          <>
            <Text fontWeight="medium">Updated at</Text>
            <Text fontFamily="mono" fontSize="sm">
              {new Date(reportedTimestamp).toLocaleString(dateTimeFormat.local)}
            </Text>
          </>
        )}
      </Grid>
    </Box>
  );
};
